import { css } from "@emotion/react";
import { useNavigate } from "react-router-dom";
import { usePostCheckout } from "@sharpana/payments/usePostCheckout";

const page = css`
  min-height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 2rem 1rem;
  color: var(--color-text);
`;

const card = css`
  width: 100%;
  max-width: 26rem;
  background: var(--color-bg-elevated);
  border: 1px solid var(--color-border);
  border-radius: 12px;
  padding: 1.75rem;
  text-align: center;

  h1 {
    margin: 0 0 0.5rem;
    font-size: 1.3rem;
  }
  p {
    margin: 0;
    color: var(--color-text-secondary);
    line-height: 1.5;
  }
`;

const spinner = css`
  width: 28px;
  height: 28px;
  margin: 0 auto 1rem;
  border: 3px solid var(--color-border-subtle);
  border-top-color: var(--color-primary);
  border-radius: 50%;
  animation: spin 0.9s linear infinite;

  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }
`;

const problem = css`
  margin-top: 1rem;
  padding: 0.75rem 1rem;
  border-radius: 8px;
  color: var(--color-warning-text);
  background: var(--color-warning-bg);
  border: 1px solid var(--color-warning-border);
  line-height: 1.5;
`;

const cta = css`
  margin-top: 1.25rem;
  background: var(--color-primary);
  color: var(--color-on-primary);
  border: 1px solid var(--color-primary-border);
  border-radius: 8px;
  padding: 0.6rem 1.25rem;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;

  &:hover {
    background: var(--color-primary-hover);
  }
`;

/**
 * Where the native wrapper lands after a successful store purchase.
 *
 * The wrapper holds a full-screen "finalizing your purchase" overlay until
 * this page reports back, so the confirm call must run on mount and must
 * notify regardless of what gets rendered — that is `usePostCheckout`'s job.
 * The route name is wire contract with
 * `mobile/shell/products/zairai.ts` (`postPurchaseRoute`).
 */
export default function RevcatPostCheckout() {
  const navigate = useNavigate();
  const { status, hasActiveSubscription } = usePostCheckout(
    "/revcat-post-checkout"
  );

  return (
    <div css={page}>
      <section css={card}>
        {status !== "error" && status !== "success" && (
          <>
            <div css={spinner} />
            <h1>Finishing up…</h1>
            <p>Confirming your purchase.</p>
          </>
        )}

        {status === "success" && (
          <>
            <h1>
              {hasActiveSubscription ? "You're subscribed" : "Purchase complete"}
            </h1>
            <p>Thanks — everything is on your account and ready to use.</p>
            <button css={cta} onClick={() => navigate("/")}>
              Start a session
            </button>
          </>
        )}

        {status === "error" && (
          <>
            <h1>Something went wrong</h1>
            <div css={problem}>
              Your payment may have gone through, but we could not confirm it.
              Please email support@mrble.ai and we will sort it out.
            </div>
            <button css={cta} onClick={() => navigate("/settings")}>
              Go to settings
            </button>
          </>
        )}
      </section>
    </div>
  );
}
